<template>
  <div class="support-page">
    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <div class="page-header mb-5 fade-in-up">
            <h1 class="display-4 fw-bold mb-4">{{ $t('support.title') }}</h1>
            <p class="text-muted">{{ $t('support.content') }}</p>
          </div>
          
          <div class="content-section">
            <div class="row g-4">
              <!-- Contact Methods -->
              <div class="col-md-6 fade-in" style="animation-delay: 0.1s;">
                <div class="card shadow-sm border-0 h-100">
                  <div class="card-body p-4">
                    <div class="support-icon mb-3">
                      <i class="bi bi-envelope"></i>
                    </div>
                    <h4 class="fw-bold mb-3">{{ $t('support.email') }}</h4>
                    <p class="text-muted mb-2">{{ $t('support.emailDescription') }}</p>
                    <p class="support-email mb-3">support@qinfu-cloud.cn</p>
                    <a href="mailto:support@qinfu-cloud.cn" class="btn btn-primary">
                      <i class="bi bi-envelope me-2"></i>{{ $t('support.sendEmail') }}
                    </a>
                  </div>
                </div>
              </div>
              
              <!-- FAQ -->
              <div class="col-md-6 fade-in" style="animation-delay: 0.2s;">
                <div class="card shadow-sm border-0 h-100">
                  <div class="card-body p-4">
                    <div class="support-icon mb-3">
                      <i class="bi bi-question-circle"></i>
                    </div>
                    <h4 class="fw-bold mb-3">{{ $t('support.faq') }}</h4>
                    <p class="text-muted mb-3">{{ $t('support.faqDescription') }}</p>
                    <a href="#faq-read" class="btn btn-outline-primary" @click="scrollToFAQ">
                      <i class="bi bi-chevron-down me-2"></i>{{ $t('support.viewFAQ') }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- FAQ Section -->
            <div class="mt-5 fade-in" style="animation-delay: 0.2s;">
              <div class="card shadow-sm border-0">
                <div class="card-body p-4">
                  <h4 class="fw-bold mb-4">{{ $t('support.faqTitle') }}</h4>
                  
                  <div class="accordion" id="faqAccordion">
                    <div id="faq-read" class="accordion-item border-0 mb-3 anchor-target">
                      <h2 class="accordion-header">
                        <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                          <strong>{{ $t('support.faqItems.read.question') }}</strong>
                        </button>
                      </h2>
                      <div id="faq1" class="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div class="accordion-body">
                          {{ $t('support.faqItems.read.answer') }}
                        </div>
                      </div>
                    </div>
                    
                    <div id="faq-types" class="accordion-item border-0 mb-3 anchor-target">
                      <h2 class="accordion-header">
                        <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                          <strong>{{ $t('support.faqItems.types.question') }}</strong>
                        </button>
                      </h2>
                      <div id="faq2" class="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div class="accordion-body">
                          {{ $t('support.faqItems.types.answer') }}
                        </div>
                      </div>
                    </div>
                    
                    <div id="faq-pricing" class="accordion-item border-0 mb-3 anchor-target">
                      <h2 class="accordion-header">
                        <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                          <strong>{{ $t('support.faqItems.pricing.question') }}</strong>
                        </button>
                      </h2>
                      <div id="faq3" class="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div class="accordion-body">
                          {{ $t('support.faqItems.pricing.answer') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Audio Guide Section -->
            <div id="audio-guide" class="mt-5 fade-in anchor-target" style="animation-delay: 0.3s;">
              <div class="card shadow-sm border-0">
                <div class="card-body p-4 p-md-5">
                  <h4 class="fw-bold mb-4 text-center">{{ $t('support.audioGuide.title') }}</h4>
                  
                  <div class="row g-4">
                    <!-- Step 1 -->
                    <div class="col-md-4">
                      <div class="guide-step text-center">
                        <div class="step-number mb-3">
                          <span class="step-badge">1</span>
                        </div>
                        <div class="step-image-wrapper mb-3">
                          <img src="/help/help/audio1.PNG" alt="Step 1" class="step-image">
                        </div>
                        <h5 class="fw-bold mb-2">{{ $t('support.audioGuide.step1.title') }}</h5>
                        <p class="text-muted small">{{ $t('support.audioGuide.step1.description') }}</p>
                      </div>
                    </div>
                    
                    <!-- Step 2 -->
                    <div class="col-md-4">
                      <div class="guide-step text-center">
                        <div class="step-number mb-3">
                          <span class="step-badge">2</span>
                        </div>
                        <div class="step-image-wrapper mb-3">
                          <img src="/help/help/audio2.PNG" alt="Step 2" class="step-image">
                        </div>
                        <h5 class="fw-bold mb-2">{{ $t('support.audioGuide.step2.title') }}</h5>
                        <p class="text-muted small">{{ $t('support.audioGuide.step2.description') }}</p>
                      </div>
                    </div>
                    
                    <!-- Step 3 -->
                    <div class="col-md-4">
                      <div class="guide-step text-center">
                        <div class="step-number mb-3">
                          <span class="step-badge">3</span>
                        </div>
                        <div class="step-image-wrapper mb-3">
                          <img src="/help/help/audio3.PNG" alt="Step 3" class="step-image">
                        </div>
                        <h5 class="fw-bold mb-2">{{ $t('support.audioGuide.step3.title') }}</h5>
                        <p class="text-muted small">{{ $t('support.audioGuide.step3.description') }}</p>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Video Tutorials -->
                  <div class="mt-5 pt-4 border-top">
                    <h5 class="fw-bold mb-3 text-center">{{ $t('support.videoGuide.title') }}</h5>
                    <p class="text-muted text-center mb-4 small">{{ $t('support.videoGuide.description') }}</p>
                    
                    <div class="row g-4 justify-content-center">
                      <!-- Bilibili Video -->
                      <div class="col-md-6">
                        <div class="video-card text-center">
                          <div class="video-icon mb-3">
                            <i class="bi bi-play-circle-fill"></i>
                          </div>
                          <h6 class="fw-bold mb-3">{{ $t('support.videoGuide.bilibili.title') }}</h6>
                          <a 
                            href="https://www.bilibili.com/video/BV18h1pB8E8p/?spm_id_from=333.788.top_right_bar_window_history.content.click" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="btn btn-primary"
                          >
                            <i class="bi bi-play-fill me-2"></i>{{ $t('support.videoGuide.bilibili.watch') }}
                          </a>
                        </div>
                      </div>
                      
                      <!-- YouTube Video (预留位置) -->
                      <div class="col-md-6" v-if="false">
                        <div class="video-card text-center">
                          <div class="video-icon mb-3">
                            <i class="bi bi-play-circle-fill"></i>
                          </div>
                          <h6 class="fw-bold mb-3">{{ $t('support.videoGuide.youtube.title') }}</h6>
                          <a 
                            href="#" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="btn btn-danger"
                          >
                            <i class="bi bi-play-fill me-2"></i>{{ $t('support.videoGuide.youtube.watch') }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Complete Guide Section -->
            <div id="complete-guide" class="mt-5 fade-in anchor-target" style="animation-delay: 0.4s;">
              <div class="card shadow-sm border-0">
                <div class="card-body p-4 p-md-5">
                  <h4 class="fw-bold mb-4 text-center">{{ $t('support.completeGuide.title') }}</h4>
                  <p class="text-muted text-center mb-4">{{ $t('support.completeGuide.description') }}</p>
                  
                  <!-- Video Tutorial -->
                  <div class="mt-4 pt-4 border-top">
                    <h5 class="fw-bold mb-3 text-center">{{ $t('support.completeGuide.bilibili.title') }}</h5>
                    
                    <div class="row g-4 justify-content-center">
                      <div class="col-md-6">
                        <div class="video-card text-center">
                          <div class="video-icon mb-3">
                            <i class="bi bi-play-circle-fill"></i>
                          </div>
                          <h6 class="fw-bold mb-3">{{ $t('support.completeGuide.bilibili.title') }}</h6>
                          <a 
                            href="https://www.bilibili.com/video/BV1s9UmBaEAe/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="btn btn-primary"
                          >
                            <i class="bi bi-play-fill me-2"></i>{{ $t('support.completeGuide.bilibili.watch') }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Steps Section (预留位置，供后续添加图片和步骤) -->
                  <div class="mt-5 pt-4 border-top">
                    <h5 class="fw-bold mb-4 text-center">{{ $t('support.completeGuide.stepsTitle') }}</h5>
                    <div class="row g-4" id="complete-guide-steps">
                      <!-- 步骤内容将在这里添加 -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Support',
  data() {
    return {
      showFAQ: true
    }
  },
  mounted() {
    // 检查URL hash并自动滚动到对应位置
    this.checkHashAndScroll()
    
    // 监听hash变化
    window.addEventListener('hashchange', this.checkHashAndScroll)
  },
  beforeUnmount() {
    window.removeEventListener('hashchange', this.checkHashAndScroll)
  },
  methods: {
    scrollToFAQ(event) {
      event.preventDefault()
      const targetElement = document.getElementById('faq-read')
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    checkHashAndScroll() {
      const hash = window.location.hash
      if (hash) {
        // 移除#号
        const targetId = hash.substring(1)
        
        // 等待DOM稳定且目标元素可用后再滚动（更可靠）
        this.$nextTick(() => {
          const tryScroll = (retries = 20) => {
            const targetElement = document.getElementById(targetId)
            if (!targetElement && retries > 0) {
              setTimeout(() => tryScroll(retries - 1), 100)
              return
            }
            if (!targetElement) return

            // 如果是FAQ相关的hash，展开对应的accordion
            if (targetId.startsWith('faq-')) {
              const accordionId = targetId.replace('faq-', 'faq')
              const accordionElement = document.getElementById(accordionId)
              if (accordionElement) {
                // eslint-disable-next-line no-undef
                const bsCollapse = new bootstrap.Collapse(accordionElement, { show: true })
                if (bsCollapse) bsCollapse.show()
              }
            }

            // 使用 scroll-margin-top 做偏移，避免来回抖动
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          tryScroll()
        })
      }
    }
  }
}
</script>

<style scoped>
.support-page {
  padding-top: calc(72px + var(--spacing-xl));
  min-height: calc(100vh - 200px);
  background: var(--color-background);
}

/* 锚点偏移，避免被固定导航遮挡 */
.anchor-target {
  scroll-margin-top: 100px;
}

.page-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.page-header h1 {
  font-size: 48px;
  line-height: 1.08349;
  font-weight: 600;
  letter-spacing: -0.003em;
  margin-bottom: var(--spacing-sm);
}

.page-header p {
  font-size: 21px;
  line-height: 1.381;
  color: var(--color-text-secondary);
}

.support-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.support-email {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-primary);
  letter-spacing: 0.01em;
}

.card {
  border-radius: var(--radius-lg);
  transition: transform 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.card h4 {
  font-size: 24px;
  line-height: 1.16667;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
}

.card p {
  font-size: 17px;
  line-height: 1.47059;
  color: var(--color-text-secondary);
}

.btn-primary {
  background: var(--color-primary);
  border: none;
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-hover);
}

.btn-outline-primary {
  border: 1px solid var(--color-primary);
  color: var(--color-primary);
  background: transparent;
}

.btn-outline-primary:hover {
  background: var(--color-primary);
  color: white;
}

.fade-in-up {
  animation: fadeInUp 0.8s ease-out;
}

.fade-in {
  animation: fadeIn 0.6s ease-out;
  opacity: 0;
  animation-fill-mode: forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.accordion-button {
  background: var(--color-background);
  border-radius: var(--radius-md) !important;
  font-size: 17px;
  font-weight: 600;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
}

.accordion-button:not(.collapsed) {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.accordion-body {
  font-size: 17px;
  line-height: 1.47059;
  color: var(--color-text-secondary);
  padding: var(--spacing-md);
}

/* Audio Guide Styles */
.guide-step {
  padding: var(--spacing-md);
  transition: transform 0.3s ease;
}

.guide-step:hover {
  transform: translateY(-5px);
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  color: white;
  border-radius: 50%;
  font-size: 20px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.step-image-wrapper {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  background: var(--color-surface);
  padding: var(--spacing-xs);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.step-image-wrapper:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.step-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--radius-sm);
}

.guide-step h5 {
  font-size: 18px;
  line-height: 1.3;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-xs);
}

.guide-step p {
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

/* Video Guide Styles */
.video-card {
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: var(--radius-lg);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.video-card:hover {
  transform: translateY(-5px);
  border-color: var(--color-primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.video-icon {
  font-size: 48px;
  color: var(--color-primary);
  transition: transform 0.3s ease;
}

.video-card:hover .video-icon {
  transform: scale(1.1);
}

.video-card h5,
.video-card h6 {
  font-size: 18px;
  line-height: 1.3;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
}

.video-card .btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: 16px;
  font-weight: 600;
  border-radius: var(--radius-md);
  transition: all 0.3s ease;
}

.video-card .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.video-card .btn-danger {
  background: #ff0000;
  border-color: #ff0000;
}

.video-card .btn-danger:hover {
  background: #cc0000;
  border-color: #cc0000;
}

@media (max-width: 734px) {
  .support-page {
    padding-top: calc(72px + var(--spacing-lg));
  }
  
  .page-header h1 {
    font-size: 32px;
    line-height: 1.125;
  }
  
  .page-header p {
    font-size: 17px;
  }
  
  .guide-step {
    margin-bottom: var(--spacing-lg);
  }
  
  .step-image-wrapper {
    max-width: 100%;
  }
  
  .guide-step h5 {
    font-size: 16px;
  }
  
  .guide-step p {
    font-size: 13px;
  }
}
</style>

